// TabPanel.tsx
import React, { FunctionComponent, useState, useEffect } from 'react';
import { styled } from '@mui/system';
import { Tabs, Tab as BaseTab, TabsList as BaseTabsList, tabClasses, buttonClasses } from '@mui/base';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';
import Icon from '../global/Icon';

const TabsList = styled(BaseTabsList, {
  shouldForwardProp: (prop) => !['$colormode', '$size'].includes(prop as string)
})<{ $colormode: 'light' | 'dark'; $size: sizeType; }>`
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: space-between;
  background-color: ${({ $size, $colormode }) => 
    $size === 'tiny' 
      ? ($colormode === 'light' ? Colors.grey1 : Colors.black) 
      : ($colormode === 'light' ? Colors.grey1 : Colors.grey9)};
  border: ${({ $size, $colormode }) => 
    $size === 'tiny' 
      ? 'none' 
      : `1px solid ${$colormode === 'light' ? Colors.grey2 : Colors.grey8}`};
  border-radius: ${({ $size }) => $size === 'tiny' ? '8px' : '40px'};
  z-index: 1000;
`;

const Tab = styled(BaseTab, {
  shouldForwardProp: (prop) => !['$colormode', '$size'].includes(prop as string)
})<{ $colormode: 'light' | 'dark'; $size: sizeType; }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  background-color: ${Colors.transparent};
  border: none;
  border-radius: ${({ $size }) => $size === 'tiny' ? '6px' : '40px'};
  padding: ${({ $size }) => 
    $size === 'tiny' ? '6px 10px' : 
    $size === 'small' ? '8px 16px' : 
    $size === 'medium' ? '10px 18px' : 
    '12px 20px'};
  margin: ${({ $size }) => $size === 'tiny' ? '2px' : '4px'};
  font-size: ${({ $size }) => 
    $size === 'tiny' ? '14px' : 
    $size === 'small' ? '14px' : 
    $size === 'medium' ? '16px' : 
    '18px'};
  color: ${({ $colormode }) => ($colormode === 'light' ? Colors.grey9 : Colors.grey1)};
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  text-align: center;
  text-wrap: nowrap;
  cursor: pointer;

  &:hover {
    background-color: ${({ $size, $colormode }) => 
      $size === 'tiny' 
        ? ($colormode === 'light' ? Colors.white+40 : Colors.grey8+40)
        : ($colormode === 'light' ? Colors.whiteTransparent : Colors.grey8+40)};
  }

  &.${tabClasses.selected} {
    background-color: ${({ $size, $colormode }) => 
      $size === 'tiny' 
        ? ($colormode === 'light' ? Colors.white : Colors.grey9) 
        : ($colormode === 'light' ? Colors.white : Colors.grey8)};
  }

  &.${buttonClasses.disabled} {
    opacity: 0.4;
    cursor: default;
  }
`;

const TabContent = styled('div')<{ $size: sizeType }>`
  display: flex;
  align-items: center;
  gap: ${({ $size }) => 
    $size === 'tiny' ? '4px' : 
    $size === 'small' ? '4px' : 
    $size === 'medium' ? '5px' : 
    '6px'};
`;

type sizeType = 'tiny' | 'small' | 'medium' | 'large';

interface TabPanelProps {
  tabs: Array<{ name: string; icon?: string; disabled?: boolean }>;
  onSelect: (selectedTabName: string) => void;
  defaultTabName?: string;
  colorMode?: 'light' | 'dark';
  size?: sizeType;
}

const TabPanel: FunctionComponent<TabPanelProps> = ({ tabs, onSelect, defaultTabName, colorMode='light', size='large' }) => {
  const defaultTabIndex = tabs.findIndex(tab => tab.name === defaultTabName);
  const [selectedTabIndex, setSelectedTabIndex] = useState(defaultTabIndex >= 0 ? defaultTabIndex : 0);

  useEffect(() => {
    setSelectedTabIndex(defaultTabIndex >= 0 ? defaultTabIndex : 0);
  }, [defaultTabName]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTabIndex(newValue);
    const selectedTab = tabs[newValue];
    if (selectedTab && onSelect) {
      onSelect(selectedTab.name);
    }
  };

  return (
    <Tabs value={selectedTabIndex} onChange={handleTabChange}>
      <TabsList $colormode={colorMode} $size={size}>
        {tabs.map((tab, index) => (
          <Tab key={index} value={index} disabled={tab.disabled} $colormode={colorMode} $size={size}>
            <TabContent $size={size}>
              {tab.icon && (
                <Icon 
                  icon={tab.icon} 
                  color={colorMode === 'light' ? Colors.grey9 : Colors.grey1} 
                  size={size === 'tiny' ? 14 : size === 'small' ? 18 : size === 'medium' ? 20 : 22} 
                />
              )}
              {tab.name}
            </TabContent>
          </Tab>
        ))}
      </TabsList>
    </Tabs>
  );
};

export default TabPanel;